/* V2
Typeahead Component with typeahead change handler and typeahead select handler
 */
import * as React from "react";
import { connect } from "react-redux";
import {
  debounce as _debounce,
  isEmpty as _isEmpty,
  map as _map
} from "lodash";
import * as $ from "jquery";
import ValidationUtils from "../../../common/utils/ValidationUtils";
import AppUtils, { scrollElementToTop } from "../utils/AppUtils";
import { AppMessages } from "../utils/AppConstants";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { EventNames, SectionContext } from "../utils/EventTrackerConstants";
import { Tracker } from "../utils/Tracker";

/**
 * Created by abhinav on 11/12/17.
 */

export interface TypeAheadElement {
  value: string;
  label: string;
  isDefault?: boolean;
  additionalInfoLabel?: string;
  others?: any;
  linkedFields?: any;
  internalKey?: any;
  additionalKey?: any;
  code?: any;
  addItem?: boolean;
  toBeAdded?: boolean;
}

export interface TypeAheadProps {
  name?: string;
  widgetName?: string;
  onKeyPress?: (event: any) => void;
  value: string;
  placeholder: string;
  className?: string;
  displayValue?: string;
  onTypeAheadChange: (input: string) => Promise<TypeAheadResults>;
  onTypeAheadSelect: (
    elem: TypeAheadElement,
    idx?: number,
    noOfKeystrokes?: number,
    event?: any,
    searchKeyword?: string
  ) => void;
  onTypeAheadBlur?: (event: any) => any;
  inputDisabled?: boolean;
  errorText?: string;
  prependGlyph?: string;
  prependOn?: string;
  highlightOn?: string;
  validator?: any;
  setInputRef?: (inputRef: any) => void;
  onTypeAheadParentBlur?: (event: any) => void;
  fieldType?: string;
  showSearchIcon?: boolean;
  keyStrokeEvent?: boolean;
  onClickHandler?: (event: any, data?: {inputType: string, inputValue?: string}) => void
}

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = DispatchProps & TypeAheadProps;

export interface TypeAheadState {
  typeAheadOptions: TypeAheadElement[];
  selectedIdx: number;
  inputValue: string;
  showDropdown: boolean;
  validationErrorText: string;
  isTyping: boolean;
  noOfStroke: number;
}

export interface TypeAheadResults {
  options: TypeAheadElement[];
  completed: boolean;
}

class TypeAhead extends React.Component<TypeAheadProps, TypeAheadState> {
  private timeoutRef: any = null;
  private inputDOM: HTMLInputElement = null;

  constructor(props: TypeAheadProps) {
    super(props);
    this.state = {
      typeAheadOptions: [],
      selectedIdx: -1,
      inputValue: "",
      showDropdown: false,
      validationErrorText: "",
      isTyping: false,
      noOfStroke: 0
    };
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onChange = this.onChange.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleTypeAheadChange = _debounce(this.handleTypeAheadChange, 300);
  }

  static contextType = SectionContext; // registering context type

  static defaultProps: Partial<Props> = {
    keyStrokeEvent: true
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.value !== this.props.value) {
      this.setState({ inputValue: this.props.value });
    }
  }

  onKeyUp(event: any): void {
    this.setState({ noOfStroke: this.state.noOfStroke + 1 });
    // Enter Clicked
    let keyCode = event.keyCode;
    if (keyCode === 13) {
      console.log("Enter Clicked");
      if (this.state.showDropdown && this.state.selectedIdx > -1) {
        let selectedOption: TypeAheadElement = this.state.typeAheadOptions[
          this.state.selectedIdx
        ];
        this.props.onTypeAheadSelect(
          selectedOption,
          this.state.selectedIdx,
          this.inputDOM.value.length,
          event,
          this.inputDOM.value
        );
        this.toggleDropdown(false, false);
      }
    } else if (keyCode === 40) {
      console.log("Arrow Down Clicked");
      let nextIdx =
        this.state.selectedIdx === this.state.typeAheadOptions.length - 1
          ? 0
          : this.state.selectedIdx + 1;
      this.setState({
        selectedIdx: nextIdx
      });
    } else if (keyCode === 38) {
      console.log("Arrow Up Clicked");
      let nextIdx =
        this.state.selectedIdx === 0
          ? this.state.typeAheadOptions.length - 1
          : this.state.selectedIdx - 1;
      this.setState({
        selectedIdx: nextIdx
      });
    } else if (event.keyCode === 27) {
      if (this.state.showDropdown) {
        this.setState({ selectedIdx: -1, showDropdown: false });
      }
    }
  }

  handleTextBoxBlur = (event: any): void => {
    const noOfStroke = this.state.noOfStroke;
    if (this.props.keyStrokeEvent && noOfStroke) {
      const value = event && event.target && event.target.value;
      this.setState({ noOfStroke: 0 });
      Tracker.track(EventNames.KeyStroke, {
        value: value,
        field: this.props.name,
        noOfKeystrokes: noOfStroke,
        widget: this.props.widgetName,
        section: this.context
      });
    }

    if (
      _isEmpty(event.relatedTarget) ||
      $(event.relatedTarget).closest(".type-ahead-dropdown-cont").length === 0
    ) {
      this.toggleDropdown(false);
      if (this.props.onTypeAheadBlur !== undefined) {
        this.props.onTypeAheadBlur(event);
      }
    }
    if (this.state.isTyping) {
      this.setState({ isTyping: false });
    }
  };

  toggleDropdown(showDropdown: boolean, isTyping?: boolean): void {
    if (AppUtils.isDefined(isTyping)) {
      this.setState({ showDropdown, isTyping });
    } else {
      this.setState({ showDropdown });
    }
  }

  onChange(event: any): void {
    let caretPosition = event.target.selectionStart;
    let value = event.target.value ? event.target.value.replace(/\\/g, "") : "";
    value = AppUtils.getNonSpecialCharString(value);
    const validationResult = ValidationUtils.checkValidationType(
      value,
      this.props.validator
    );
    if (validationResult && validationResult.result === true) {
      this.setState({ validationErrorText: "" });
    } else {
      this.setState({
        validationErrorText: validationResult
          ? validationResult.errorMessage
          : ""
      });
    }
    if (!AppUtils.containsOnlyAsciiChar(value)) {
      this.props.orionClientLogger({
        message: AppMessages.ERROR_SPECIAL_CHARACTERS,
        value,
        id: this.props.name
      });
    }
    if (value.length === 1) value = value.toUpperCase();
    else if (value.length > 1)
      value = value
        .split(" ")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
        .split(".")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(".");

    event.target.value = value;
    this.setState({ inputValue: event.target.value, isTyping: true }, () => {
      this.setCaretPosition(this.inputDOM, caretPosition);
    });

    this.handleTypeAheadChange(event.target.value);
  }

  handleTypeAheadChange = (value: string) => {
    this.props.onTypeAheadChange(value).then(
      (data: TypeAheadResults) => {
        this.onTypeAheadResults(data.options);
      },
      () => {
        console.log("Failed to fetch typeahead options");
      }
    );
  };

  onTypeAheadResults(options: TypeAheadElement[]): void {
    this.setState({
      showDropdown: true,
      typeAheadOptions: options,
      selectedIdx: 0
    });
  }

  onSelect(event: any, idx: number): void {
    if (this.isValidIndex(idx)) {
      // this.setState({
      //     selectedIdx: idx
      // });
      let selectedOption: TypeAheadElement = this.state.typeAheadOptions[idx];
      if (this.props.onClickHandler) {
        this.props.onClickHandler(event, {inputType: "Select - Autosuggest", inputValue: selectedOption.label})
      }
      LocalStorageUtils.setClickEvents({
        value: selectedOption.label,
        field: this.props.name,
        noOfKeystrokes: this.inputDOM.value.length,
        widget: this.props.widgetName,
        section: this.context
      });
      this.props.onTypeAheadSelect(
        selectedOption,
        idx,
        this.inputDOM.value.length,
        event,
        this.inputDOM.value
      );
      this.toggleDropdown(false, false);
    }
  }

  scrollToTop = () => {
    if (AppUtils.getIsMobile()) {
      scrollElementToTop(
        this.inputDOM as HTMLElement,
        document.querySelector(
          ".ongoing-consultation-cont"
        ) as HTMLElement,
        60);
    }
  }

  isValidIndex(idx: number): boolean {
    return idx > -1 && idx < this.state.typeAheadOptions.length;
  }

  setCaretPosition(element: any, caretPos: number) {
    let elem = element;

    if (elem != null) {
      if (elem.createTextRange) {
        let range = elem.createTextRange();
        range.move("character", caretPos);
        range.select();
      } else {
        if (elem.selectionStart) {
          elem.focus();
          elem.setSelectionRange(caretPos, caretPos);
        } else elem.focus();
      }
    }
  }

  handleInputClick = (event: any, data?: {inputType?: string, inputValue?: string}) => {
    if (this.props.onClickHandler) {
      this.props.onClickHandler(event, {...data, inputType: "Search"})
    }
  }

  render() {
    let wrapperCls = this.state.showDropdown ? "with-dropdown" : "";
    let myInstance = this;
    const errorText = !_isEmpty(this.props.errorText)
      ? this.props.errorText
      : null;
    const inputValue = this.state.isTyping
      ? AppUtils.getNonSpecialCharString(this.state.inputValue || "")
      : AppUtils.getNonSpecialCharString(this.props.value || "");
    const displaySearchIcon = AppUtils.isDefined(this.props.showSearchIcon) && this.props.showSearchIcon;
    return (
      <div className={wrapperCls}>
        <div className="ta-cont">
          <div className="dropdown-cont type-ahead-dropdown-cont">
            {this.props.value !== "" &&
              _map(
                this.state.typeAheadOptions,
                (option: TypeAheadElement, index: number) => {
                  let cssClass =
                    myInstance.state.selectedIdx === index ? "selected" : "";
                  let highlightOption =
                    !_isEmpty(myInstance.props.highlightOn) &&
                      !_isEmpty(option.others) &&
                      option.others.hasOwnProperty(
                        myInstance.props.highlightOn
                      ) &&
                      option.others[myInstance.props.highlightOn]
                      ? "highlighted"
                      : "";
                  let prependGlyph =
                    !_isEmpty(myInstance.props.prependGlyph) &&
                    !_isEmpty(myInstance.props.prependOn) &&
                    option.others.hasOwnProperty(myInstance.props.prependOn) &&
                    option.others[myInstance.props.prependOn];
                  let additionalTextToDisplay: string = "";
                  if (option.others && option.others.prescribableProducts && option.others.prescribableProducts.subProducts
                    && option.others.prescribableProducts.subProducts.length) {
                    additionalTextToDisplay = option.others.prescribableProducts.subProducts.length;
                  }
                  return (
                    <div
                      className={`ta-option ${cssClass} ${highlightOption}`}
                      onClick={event => {
                        clearTimeout(myInstance.timeoutRef);
                        myInstance.onSelect(event, index);
                      }}
                      key={index}
                      tabIndex={0}
                      data-index={option.value}
                    >
                      {prependGlyph && (
                        <span
                          className={`glyphicon glyphicon-ok ${
                            prependGlyph ? "" : "hide-glyph"
                            }`}
                        ></span>
                      )}
                      {option.label} {additionalTextToDisplay && <span className="sub-product-length">{additionalTextToDisplay}</span>}
                      {option.isDefault !== undefined && option.isDefault && (
                        <span className={"default-typeahead-option"}>
                          (default)
                        </span>
                      )}
                      {!_isEmpty(option.additionalInfoLabel) && (
                        <span className={"additional-typeahead-info"}>
                          {" "}
                          | {option.additionalInfoLabel}
                        </span>
                      )}
                    </div>
                  );
                }
              )}
          </div>
          <div className={"ta-input-wrapper text-input"}>
            {displaySearchIcon && (
              <span className={"icon-container"}>
                <img src="/images/search.svg" />
              </span>
            )}
            <input
              className={`input-box ${displaySearchIcon ? "left-pad" : ""} `}
              type="text"
              name={this.props.name}
              placeholder={this.props.placeholder ? this.props.placeholder : ""}
              ref={inputDOM => {
                this.inputDOM = inputDOM;
                if (this.props.setInputRef) {
                  this.props.setInputRef(inputDOM);
                }
              }}
              value={inputValue}
              onFocus={() => { this.toggleDropdown(true) }}
              onBlur={this.handleTextBoxBlur}
              onKeyUp={this.onKeyUp}
              onChange={this.onChange}
              autoComplete="off"
              disabled={this.props.inputDisabled}
              onClick={(event) => this.handleInputClick(event, {inputValue})}
            ></input>

            {!_isEmpty(this.state.validationErrorText) && (
              <span className="validation-error">
                {this.state.validationErrorText}
              </span>
            )}
          </div>
          {!_isEmpty(errorText) && (
            <div className={"type-ahead-error"}>{errorText}</div>
          )}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = () => {
  return {
    orionClientLogger: (err: any) => {
      return OngoingConsultationActions.clientLogger(err);
    }
  };
};

export default connect<null, DispatchProps, TypeAheadProps>(
  null,
  mapDispatchToProps
)(TypeAhead);
